import { useMemo, useState, type CSSProperties } from "react";
import { ActivityRow } from "../cockpit/ActivityPanel";
import { LogActivityModal } from "./LogActivityModal";
import { useActivitiesFor } from "../../hooks/use-activities";
import { CHANNEL_ICON, CHANNEL_LABEL, CHANNEL_OPTIONS } from "../../lib/activity-display";
import type { Activity, ActivityChannel, ActivityDirection, RowActivitySlice } from "../../../lib/types";

interface RowActivitySectionProps {
  entityTypeSlug: string;
  entityId: string;
  /** The embedded slice from get_row_detail (top 10 + total + cursor). */
  slice: RowActivitySlice;
}

/**
 * Sprint 14 — a row's interaction history. Renders the embedded `activities`
 * slice from get_row_detail (mirroring RowFilesSection's use of `owned`). "+ Log
 * activity" writes via create_activity with this row pre-filled as the primary
 * regarding target; "View all" pages through list_activities_for.
 */
export function RowActivitySection({ entityTypeSlug, entityId, slice }: RowActivitySectionProps) {
  const [logOpen, setLogOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [channelFilter, setChannelFilter] = useState<ActivityChannel | null>(null);
  const [directionFilter, setDirectionFilter] = useState<ActivityDirection | null>(null);

  // "View all" loads the full (paginated) list; otherwise we render the embedded slice.
  const fullQuery = useActivitiesFor(showAll ? { kind: "entity", type: entityTypeSlug, id: entityId } : undefined, 50);
  const source: Activity[] = showAll ? fullQuery.data ?? slice.items : slice.items;

  const items = useMemo(
    () =>
      source.filter(
        (a) =>
          (!channelFilter || a.channel === channelFilter) &&
          (!directionFilter || a.direction === directionFilter),
      ),
    [source, channelFilter, directionFilter],
  );

  // Only show channel chips that actually occur in the data.
  const presentChannels = useMemo(() => {
    const set = new Set(source.map((a) => a.channel));
    return CHANNEL_OPTIONS.filter((c) => set.has(c));
  }, [source]);

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={() => setLogOpen(true)} style={actionButtonStyle}>
          <i className="ti ti-plus" style={{ fontSize: 13 }} aria-hidden="true" /> Log activity
        </button>
        {presentChannels.length > 1 && (
          <div style={{ display: "flex", gap: 4, marginLeft: 4, flexWrap: "wrap" }}>
            {presentChannels.map((c) => {
              const on = channelFilter === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setChannelFilter(on ? null : c)}
                  style={chipStyle(on)}
                  title={CHANNEL_LABEL[c]}
                >
                  <i className={`ti ${CHANNEL_ICON[c]}`} style={{ fontSize: 12 }} aria-hidden="true" /> {CHANNEL_LABEL[c]}
                </button>
              );
            })}
          </div>
        )}
        <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
          {(["inbound", "outbound"] as ActivityDirection[]).map((d) => {
            const on = directionFilter === d;
            return (
              <button key={d} type="button" onClick={() => setDirectionFilter(on ? null : d)} style={chipStyle(on)}>
                {d}
              </button>
            );
          })}
        </div>
      </div>

      {items.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", padding: "6px 0" }}>
          {slice.total === 0 ? "No activity logged for this record yet." : "No activity matches the current filters."}
        </div>
      ) : (
        items.map((a) => <ActivityRow key={a.id} activity={a} deletable />)
      )}

      {!showAll && slice.total > slice.items.length && (
        <button type="button" onClick={() => setShowAll(true)} style={viewAllStyle}>
          View all ({slice.total})
        </button>
      )}

      <LogActivityModal
        open={logOpen}
        onClose={() => setLogOpen(false)}
        entityTypeSlug={entityTypeSlug}
        entityId={entityId}
      />
    </div>
  );
}

const actionButtonStyle: CSSProperties = {
  fontSize: 12,
  padding: "4px 10px",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  whiteSpace: "nowrap",
};

const chipStyle = (on: boolean): CSSProperties => ({
  fontSize: 11,
  padding: "3px 8px",
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  border: on ? "1px solid var(--c-teal-600)" : "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-pill, 999px)",
  background: on ? "var(--c-teal-600)" : "var(--color-background-primary)",
  color: on ? "#fff" : "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  whiteSpace: "nowrap",
});

const viewAllStyle: CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--color-text-info)",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 500,
  padding: "6px 2px",
  fontFamily: "var(--font-sans)",
};
